import { PlayerModel } from '@toilet-paper-games/core';
import { action, reaction, toJS } from 'mobx';

import { Connection } from '@/common/models/Connection';
import {
  NetworkDataTransfer,
  NetworkDataType,
} from '@/common/models/NetworkDataTransfers';
import { idPrefix, NetworkHandler } from '@/common/models/NetworkHandler';
import { generateName } from '@/common/utils/code-gen';
import { makeSimpleAutoObservable } from '@/common/utils/mobx/mobx';
import { createPeer } from '@/common/utils/peerjs';
import { SessionStorageKeys } from './HosterAppModel';
import { ConnectionMetadata } from '@/controller/models/ControllerNetworkHandler';
import { UsersManager } from '@/hoster/models/UsersManager';
import { HosterUserModel } from './HosterUserModel';

export class HosterNetworkHandler extends NetworkHandler {
  /** This map contains all players that ever connected, you must filter each player on the active property to get online players */
  userManager = new UsersManager();

  connectionListeners: {
    listener: (user: string) => void;
  }[] = [];
  disconnectionListeners: {
    listener: (user: string) => void;
  }[] = [];

  constructor(public uuid: string) {
    super(uuid);

    makeSimpleAutoObservable(this, {}, { autoBind: true });

    const hookLocalConnection = () => {
      this.localConnection.on(
        'open',
        action(() => {
          this.networkReady = true;
          this.uuid = this.localConnection.id;
        }),
      );

      this.localConnection.on(
        'connection',
        action((peerConnection) => {
          const metadata = peerConnection.metadata as ConnectionMetadata;

          const controllerId = metadata.userId;

          const connection = new Connection(
            controllerId,
            peerConnection,
            this.localConnection.id,
          );

          let user = this.userManager.usersMap.get(controllerId);

          if (!user) {
            user = new HosterUserModel(this.userManager, controllerId);
            this.userManager.usersMap.set(controllerId, user);
          } else {
            user.reconnect();
          }

          user.connection = connection;

          this.alertConnectionListeners(controllerId);

          connection.raw.on(
            'close',
            action(() => {
              this.alertDisconnectionListeners(connection.userId);

              const user = this.userManager.usersMap.get(connection.userId);

              if (!user) return;

              user.connection = null;

              connection.raw.removeAllListeners();
            }),
          );

          connection.raw.on('data', (data) => {
            this.onData(data);
            connection?.onData(data);
          });
        }),
      );

      this.localConnection.on(
        'error',
        action((err) => {
          // Ensures that the id is unique
          if ('type' in err && err.type === 'unavailable-id') {
            this.uuid = generateName();
            sessionStorage.setItem(SessionStorageKeys.UUID, this.uuid);

            this.localConnection = createPeer(
              idPrefix + this.uuid,
              this.peerJsErrorHandler,
            );
            hookLocalConnection();
          }
        }),
      );

      this.addDataListener((data) => {
        console.log(data);
      }, NetworkDataType.DEBUG);
    };

    this.addDataListener((data) => {
      const user = this.userManager.usersMap.get(data.userId);
      if (!user) return;
      user.screenName = data.data.screenName;
    }, NetworkDataType.UPDATE_USER_NAME);

    hookLocalConnection();
  }

  alertConnectionListeners(controllerId: string) {
    for (const { listener } of this.connectionListeners) {
      listener(controllerId);
    }
  }

  addConnectionListener(listener: (user: string) => void) {
    const handler = { listener };

    this.connectionListeners.push(handler);

    return () => {
      const index = this.connectionListeners.indexOf(handler);
      if (index === -1) return;
      this.connectionListeners.splice(index, 1);
    };
  }

  alertDisconnectionListeners(userId: string) {
    this.disconnectionListeners.forEach(({ listener }) => listener(userId));
  }

  addDisconnectionListener(listener: (user: string) => void) {
    const handler = { listener };

    this.disconnectionListeners.push(handler);

    return () => {
      const index = this.disconnectionListeners.indexOf(handler);
      if (index === -1) return;
      this.disconnectionListeners.splice(index, 1);
    };
  }

  broadcast(data: NetworkDataTransfer) {
    this.userManager.connectedUsers.forEach((user) => user.connection!.send(data));
  }
}
